import React, { useEffect, useState, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { fetchAllAdminUsersAsync, fetchAllUserRolesAsync, searchAdminUsersAsync, selectAdminUserStatus, selectAllAdminUsers } from './AdminUserSlice'
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, TablePagination, TableSortLabel, Backdrop, CircularProgress,
  TextField, Box, InputAdornment, IconButton
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'

const AdminUser = () => {
  const dispatch = useAppDispatch()
  const adminUsers = useAppSelector(selectAllAdminUsers)
  const status = useAppSelector(selectAdminUserStatus)
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(1000)
  const [sort, setSort] = useState('userEmail,asc')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [searchQuery])

  // Handle search or fetch all users
  useEffect(() => {
    if (debouncedSearchQuery) {
      dispatch(searchAdminUsersAsync({ query: debouncedSearchQuery, page: { page, size, sort } }))
    } else {
      dispatch(fetchAllAdminUsersAsync({ mode: 'regular', page: { page, size, sort } }))
    }
  }, [debouncedSearchQuery, page, size, sort])

  // Fetch user roles only once on mount
  useEffect(() => {
    dispatch(fetchAllUserRolesAsync({ mode: 'regular' }))
  }, [])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSize(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSort = (column: string) => {
    const isAsc = sort === `${column},asc`
    setSort(`${column},${isAsc ? 'desc' : 'asc'}`)
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
    setPage(0) // Reset to first page when searching
  }

  const clearSearch = () => {
    setSearchQuery('')
    setPage(0)
  }

  const isLoading = status === 'loading'

  return (<div>
    <Backdrop
      sx={{
        color: '#fff',
        zIndex: (theme) => theme.zIndex.drawer + 1
      }}
      open={isLoading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
    <h1>Admin User</h1>

    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
      <TextField
        label="Search by Email or User ID"
        variant="outlined"
        value={searchQuery}
        onChange={handleSearch}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment position="end">
              <IconButton onClick={clearSearch} edge="end">
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          )
        }}
      />
    </Box>

    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sort.startsWith('userEmail')}
                direction={sort.endsWith('asc') ? 'asc' : 'desc'}
                onClick={() => handleSort('userEmail')}
              >
                User Email
              </TableSortLabel>
            </TableCell>
            <TableCell>User Roles</TableCell>
            <TableCell>
              <TableSortLabel
                active={sort.startsWith('paidTier')}
                direction={sort.endsWith('asc') ? 'asc' : 'desc'}
                onClick={() => handleSort('paidTier')}
              >
                Paid Tier ID
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sort.startsWith('paidTierCount')}
                direction={sort.endsWith('asc') ? 'asc' : 'desc'}
                onClick={() => handleSort('paidTierCount')}
              >
                Credit Remaining
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sort.startsWith('paidTierExpiry')}
                direction={sort.endsWith('asc') ? 'asc' : 'desc'}
                onClick={() => handleSort('paidTierExpiry')}
              >
                Credit Expiration
              </TableSortLabel>
            </TableCell>
            <TableCell> </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {adminUsers.content?.map((row) => (
            <TableRow key={row.userId}>
              <TableCell>{row.userEmail}</TableCell>
              <TableCell sx={{ whiteSpace: "normal", wordWrap: "break-word" }}>{row.role.split(',').map(e => e.trim()).join(', \n')}</TableCell>
              <TableCell>{row.paidTier}</TableCell>
              <TableCell>{row.paidTierCount}</TableCell>
              <TableCell>{format(new Date(row.paidTierExpiry * 1000), 'yyyy-MM-dd')}</TableCell>
              <TableCell><Button onClick={() => navigate(`/admin/users/uid/${row.userId}`)}>Edit</Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100, 1000, 5000]}
        component="div"
        count={adminUsers.totalElements || 0}
        rowsPerPage={size}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  </div>)
}

export default AdminUser